import React, { forwardRef } from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

import type { FieldBaseProps, SelectOption } from './field.types';
import { FieldShell } from './FieldShell';
import { useFieldIds } from './useFieldIds';

export interface CheckboxGroupInputProps<T extends string | number> extends FieldBaseProps {
  options: readonly SelectOption<T>[];
  value: readonly T[];
  onChange: (value: T[], event: React.ChangeEvent<HTMLInputElement>) => void;
  row?: boolean;
  name?: string;
}

function CheckboxGroupInputInner<T extends string | number>(props: CheckboxGroupInputProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const { id: idProp, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx, options, value, onChange, row, name } = props;
  const ids = useFieldIds(idProp);
  return (
    <FieldShell ref={ref} kind="group" describedById={describedById} ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={size} fullWidth={fullWidth} className={className} sx={sx}>
      <FormGroup row={row}>
        {options.map((o) => (
          <FormControlLabel
            key={String(o.value)}
            label={o.label}
            disabled={o.disabled}
            control={
              <Checkbox
                size={size}
                name={name}
                value={String(o.value)}
                checked={value.includes(o.value)}
                inputProps={{ 'aria-invalid': error || undefined }}
                onChange={(event, checked) => onChange(checked ? [...value, o.value] : value.filter((v) => v !== o.value), event)}
              />
            }
          />
        ))}
      </FormGroup>
    </FieldShell>
  );
}

export const CheckboxGroupInput = forwardRef(CheckboxGroupInputInner) as <T extends string | number>(
  props: CheckboxGroupInputProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
